import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Container } from "reactstrap";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { withTranslation, useTranslation } from "react-i18next";
import { CardShowOff } from "agronomist-ui-components";
import "./Section.scss";
import { SwiperSlide } from "swiper/react";
import "swiper/swiper.min.css";
import { connect } from "react-redux";
import { compose } from "recompose";
import { PhotoModal } from "../../common/ShowOffField/Components";
import AddPhotoButton from "../../common/AddPhotoButton/AddPhotoButton";
import Section from "./Section";

import { selectLatestPhotos } from "../../../services/redux/photos/selectors";
import {
  getPhotos,
  voteOnPhoto,
  getPopularPhotos,
  unvoteOnPhoto,
} from "../../../services/redux/photos/reducer";
import { GET_PHOTOS_LATEST } from "../../../services/redux/photos/actions";
import { Loader } from "../../common";
import {
  hasPhotoVoted,
  userIsLogin,
} from "../../common/ShowOffField/Components/VoteWithoutAuth/VoteWithoutAuth";
import { getAuthorNameOfPhoto } from "../../common/ShowOffField/helpers";
import { CarouselWrapper } from "./CarouselWrapper";

const ShowOffCarousel = ({
  className,
  isCarousel = true,
  articlesLimit = 8,
  background = "light", // "light" | "dark"
  slidesPerView,
  getPhotos,
  voteOnPhoto,
  unvoteOnPhoto,
}) => {
  const { t } = useTranslation(["widgets"]);
  const sliderOn = isCarousel || window.innerWidth < 1200;
  const [isLoading, setIsLoading] = useState(true);
  const [latest, setLatest] = useState([]);
  const userLoggedIn = userIsLogin();
  const [selectedPopupVisible, setSelectedPopupVisible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [photoToLike, setPhotoToLike] = useState(null);
  const swiper = useRef(null);

  useEffect(() => {
    getPhotos({
      params: {
        ordering: "-published_at",
        publish_status: "2",
        limit: articlesLimit,
      },
      type: GET_PHOTOS_LATEST,
    }).then(data => {
      setIsLoading(false);
      if (data.payload.data) {
        setLatest(data.payload.data.results);
      }
    });
  }, []);

  useEffect(() => {
    if (!!selected === false) {
      return;
    }

    const selectedIndexInLatest = latest.findIndex(
      item => item.id === selected.id
    );
    if (selectedIndexInLatest === -1) {
      return;
    }

    const newLatest = [...latest];
    newLatest[selectedIndexInLatest] = selected;
    setLatest(newLatest);
  }, [selected]);

  const actionBar = (
    <div className="Sections__tags">
      <AddPhotoButton />
    </div>
  );

  const onVoteWithoutAuth = photo => {
    setSelected(photo);
    setPhotoToLike(photo);
    setShowLogin(true);
  };

  return (
    <>
      <div className={`Sections--${background}`}>
        <Container>
          <div className="Sections__heading">
            <h2>{t("widgets:gallery.title")}</h2>
            <div className="Sections__subtitle">
              {t("widgets:gallery.subtitle")}
            </div>
          </div>
          <Section
            className={classNames({
              className,
              "has-categories": true,
            })}
          >
            <>
              <div className="Sections__categories has-categories">
                {actionBar}
                {
                  <Link className="Sections__more" to="/galeria">
                    {t("widgets:gallery.seeAll")}
                  </Link>
                }
              </div>

              <CarouselWrapper
                showNav={latest.length > 4 || window.innerWidth < 1200}
                showPag={latest.legnth > 4 || window.innerWidth < 1200}
                slidesPerViewMed={4}
                slidesPerViewBig={slidesPerView || 4}
                swiperRef={swiper}
                isSliderOn={sliderOn}
              >
                {isLoading ? (
                  <Loader />
                ) : (
                  latest.map((el, index) => {
                    return (
                      <SwiperSlide
                        key={`showOff-slide-${el.id}`}
                        style={{ height: "auto" }}
                      >
                        <CardShowOff
                          key={`showOff-card-${el.id}`}
                          author={getAuthorNameOfPhoto(el)}
                          authorLabel={t("widgets:photo.author")}
                          imageUrl={el.image_thumbnail}
                          title={el.title}
                          isLiked={hasPhotoVoted(el, userLoggedIn)}
                          isRewarded={!!el.rewards.length}
                          isRewardedLabel={t("widgets:photo.rewarded")}
                          starsCount={el.votes_count}
                          isHigher={(index + 1) % 2 == 0}
                          onClick={() => {
                            setSelected(el);
                            setSelectedPopupVisible(true);
                          }}
                          onVoteClick={() => {
                            if (!userLoggedIn) {
                              onVoteWithoutAuth(el);
                              return;
                            }

                            if (el.voted) {
                              unvoteOnPhoto(el);
                            } else {
                              voteOnPhoto(el);
                            }

                            const newLatest = [...latest];
                            newLatest[index] = {
                              ...newLatest[index],
                              voted: !newLatest[index].voted,
                              votes_count: newLatest[index].voted
                                ? newLatest[index].votes_count - 1
                                : newLatest[index].votes_count + 1,
                            };
                            setLatest(newLatest);
                          }}
                        />
                      </SwiperSlide>
                    );
                  })
                )}
              </CarouselWrapper>
            </>
          </Section>
        </Container>
      </div>

      <PhotoModal
        isOpen={selected !== null}
        onRequestClose={() => {
          setSelected(null);
        }}
        onVoteWithoutAuth={onVoteWithoutAuth}
        voteOnPhoto={voteOnPhoto}
        unvoteOnPhoto={unvoteOnPhoto}
        showLogin={showLogin}
        setShowLogin={setShowLogin}
        selected={selected}
        setSelected={setSelected}
        photoToLike={photoToLike}
        setPhotoToLike={setPhotoToLike}
        latest={latest}
        setLatest={setLatest}
        selectedPopupVisible={selectedPopupVisible}
        setSelectedPopupVisible={setSelectedPopupVisible}
      />
    </>
  );
};

ShowOffCarousel.propTypes = {
  withWidget: PropTypes.bool,
};

ShowOffCarousel.defaultProps = {
  withWidget: false,
};

export default compose(
  withTranslation(["widgets"]),
  connect(
    state => ({
      latest: selectLatestPhotos(state),
    }),
    {
      voteOnPhoto,
      unvoteOnPhoto,
      getPhotos,
      getPopularPhotos,
    }
  )
)(ShowOffCarousel);
